// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getShipments } from '@/shipment/api/get-shipments';
import type { ShipmentFilter } from '@/shipment/api/get-shipments';
import { shipmentsQueryKey } from '@/shipment/hooks/use-shipments';
import { estimateResyncSeconds } from '@/shipment/utilities/estimate-resync-time';
import { useQuery } from '@tanstack/react-query';

export type ResyncOrderType = 'purchase' | 'return' | 'fulfillment';

interface ResyncEstimate {
  // Estimated resync duration in seconds; 0 until shipments load, so callers must
  // gate any block decision on `isReady` rather than treating 0 as "fast".
  estimateSeconds: number;
  // False until the shipment query resolves. While false the estimate is unknown,
  // so manual resync must stay blocked to keep large orders from slipping past the cap.
  isReady: boolean;
  // True when the shipment query failed; the estimate will never become ready, so
  // callers should explain the blocked resync instead of showing a dead disabled button.
  isError: boolean;
}

function filterForOrder(
  orderType: ResyncOrderType,
  orderNumber: string
): ShipmentFilter {
  switch (orderType) {
    case 'purchase':
      return { poNumbers: [orderNumber] };
    case 'return':
      return { roNumbers: [orderNumber] };
    case 'fulfillment':
      return { foNumbers: [orderNumber] };
  }
}

/**
 * Estimated resync duration for an order, with a readiness flag. Shipments are fetched only while
 * `enabled` (e.g. the resync surface is open or a resync is running), so the header stays cheap
 * otherwise. `isReady` is false until the fetch resolves so callers can keep manual resync blocked
 * while the estimate is unknown.
 */
export function useResyncEstimate(
  orderType: ResyncOrderType,
  orderNumber: string,
  enabled: boolean
): ResyncEstimate {
  const query = useQuery({
    queryKey: [...shipmentsQueryKey, 'resync-estimate', orderType, orderNumber],
    // TODO(#15946): Count units across every shipment page when an order can exceed
    // the default page size of 50 shipments — this first-page estimate undercounts and
    // lets such orders slip past the manual-resync cap
    queryFn: () => getShipments(filterForOrder(orderType, orderNumber)),
    enabled,
  });
  return {
    estimateSeconds: estimateResyncSeconds(query.data?.shipments ?? []),
    isReady: query.isSuccess,
    isError: query.isError,
  };
}
